/**
 * Maps auth API errors (axios error objects) to clear, user-facing messages.
 *
 * The V2 backend returns errors as `{ error: "..." }` (sometimes with extra
 * fields like `password_errors`). These helpers translate status codes and
 * payloads into friendly, specific messages instead of generic fallbacks.
 */

export const OFFLINE_MESSAGE =
  'You appear to be offline. Check your connection and try again.';
export const RATE_LIMIT_MESSAGE =
  'Too many attempts. Please wait a moment and try again.';

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/** Pull the backend-provided message from an axios error, if any. */
const backendMessage = (error) => {
  const data = error?.response?.data || {};
  return data.error || data.detail || data.message || null;
};

/** Friendly message for a failed login attempt. */
export function getLoginErrorMessage(error) {
  if (isOffline()) return OFFLINE_MESSAGE;

  const status = error?.response?.status;

  // No response at all => network/connection problem.
  if (!error?.response) return OFFLINE_MESSAGE;

  if (status === 401) return 'Incorrect email or password. Please try again.';
  if (status === 400) {
    return backendMessage(error) || 'Please enter both your email and password.';
  }
  if (status === 429) return RATE_LIMIT_MESSAGE;
  if (status >= 500) {
    return 'Something went wrong on our end. Please try again in a moment.';
  }
  return (
    backendMessage(error) ||
    'Login failed. Please check your credentials and try again.'
  );
}

/** Friendly message for a failed registration attempt. */
export function getRegisterErrorMessage(error) {
  if (isOffline()) return OFFLINE_MESSAGE;

  const status = error?.response?.status;
  const data = error?.response?.data || {};

  if (!error?.response) return OFFLINE_MESSAGE;

  if (status === 409) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  // Surface specific password problems if the server sent them.
  if (
    status === 400 &&
    Array.isArray(data.password_errors) &&
    data.password_errors.length > 0
  ) {
    return data.password_errors.join('. ');
  }
  if (status === 400) {
    return backendMessage(error) || 'Please check the form and try again.';
  }
  if (status === 429) return RATE_LIMIT_MESSAGE;
  if (status >= 500) {
    return "We couldn't create your account right now. Please try again in a moment.";
  }
  return backendMessage(error) || 'Registration failed. Please try again.';
}
